import { create } from "zustand";

import { BILLING_ENABLED } from "./billingConfig";
import type { CommunicationPreference } from "./types";

type SessionState = {
  accessToken: string;
  setAccessToken: (accessToken: string) => void;
  // "For dummies" (novice) view (2026-06-08). The merchant's GLOBAL
  // communication preference. `"follow"` (default) defers to each agent's
  // `default_communication_level`; `"expert"` / `"novice"` force that level
  // everywhere. Persisted to localStorage so the choice survives reloads.
  communicationPreference: CommunicationPreference;
  setCommunicationPreference: (pref: CommunicationPreference) => void;
  // Whether billing surfaces (Facturación tab, pricing, hire billing copy) are
  // shown. The AUTHORITATIVE source is the backend bootstrap `billing_enabled`
  // (= billing_configured() server-side), synced once the session is ready, so
  // an unconfigured backend never shows a billing UI it can't back. We seed it
  // from the build-time `VITE_BILLING_ENABLED` flag only as a pre-bootstrap
  // default (avoids a flash in prod where both agree).
  billingEnabled: boolean;
  setBillingEnabled: (enabled: boolean) => void;
};

// App-JWT persistence (2026-06-13). The Nexo session token is a ONE-TIME
// bootstrap: the backend's replay guard rejects re-exchanging the same token
// within 60s, and TN's `getSessionToken` hands back the SAME still-valid token
// within its ~5 min window. So when the embedded iframe is torn down and
// re-created (merchant navigates away in the TN admin and comes back), an
// in-memory-only token forced a re-exchange that hit the replay guard →
// "Nexo session token already used". We persist the exchanged app JWT to
// sessionStorage instead: on come-back we reuse the still-valid JWT and skip
// the exchange entirely. sessionStorage (not localStorage) keeps it scoped to
// the tab session — it survives iframe reloads within the tab but clears when
// the tab closes, which is the right lifetime for a session credential. After
// the 30 min access TTL the persisted token is dropped here as expired and the
// 401 interceptor / SessionGate exchanges a (by then genuinely fresh) token.
const ACCESS_TOKEN_KEY = "an_access_token";

function isJwtUsable(token: string): boolean {
  try {
    const segment = token.split(".")[1];
    if (!segment) return false;
    // base64url → base64 (+ restore the padding `atob` requires).
    let b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    b64 += "=".repeat((4 - (b64.length % 4)) % 4);
    const decoded = JSON.parse(atob(b64)) as { exp?: number };
    if (typeof decoded.exp !== "number") return true; // no exp → assume usable
    // 10s skew so we never hand back a token that dies mid-request.
    return decoded.exp * 1000 > Date.now() + 10_000;
  } catch {
    return false; // unparseable → treat as unusable, force a fresh exchange
  }
}

function readPersistedAccessToken(): string {
  try {
    const stored = window.sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (stored && isJwtUsable(stored)) return stored;
    if (stored) window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // sessionStorage unavailable (private mode / partitioned iframe / tests).
  }
  return "";
}

function writePersistedAccessToken(token: string): void {
  try {
    if (token) window.sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    else window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // Non-fatal: persistence is best-effort.
  }
}

// Seed `accessToken` from `VITE_DEV_ACCESS_TOKEN` ONLY when we're running
// standalone (i.e., NOT inside the TiendaNube admin iframe). Inside the real
// iframe we must start with no token so the Dashboard's Nexo session-token
// exchange writes the per-store JWT first. Pre-seeding here would mean every
// API call would carry the hardcoded dev JWT (store_id=1) instead of the real
// store's token — root cause of "page loads but not in dashboard" (2026-05-11).
// Inside the iframe we instead rehydrate the last still-valid exchanged JWT
// from sessionStorage so navigating back doesn't re-exchange the Nexo token.
const DEV_TOKEN = import.meta.env.VITE_DEV_ACCESS_TOKEN as string | undefined;
const IN_IFRAME =
  typeof window !== "undefined" && window.location !== window.parent.location;
const INITIAL_TOKEN =
  DEV_TOKEN && !IN_IFRAME ? DEV_TOKEN : readPersistedAccessToken();

// communicationPreference persistence (2026-06-08; default flipped 2026-06-14).
// Read once on init; write on every set. Guarded so SSR / non-browser test runs
// (no `localStorage`) degrade to the in-memory default without throwing.
const COMM_PREF_KEY = "an_communication_preference";

// Simple-as-default (plans/18 P1-13 / Leo S4 `LF-X02`, §6-D1). A first-time
// merchant with NO stored preference opens the dashboard in SIMPLE ("novice")
// rather than deferring to each agent's `default_communication_level`. We
// return "novice" (not "follow") so the global pref short-circuits the agent
// default in `useCommunicationLevel` — every card renders Simple on a fresh
// install. An explicit per-card Experto toggle still wins (it sets a per-card
// `override` that beats the global pref), and once the merchant picks any
// level it is persisted here and honoured on reload. The Simple text is the
// server-side Gemini-regenerated `novice.py` body, so the P0 leak-scrub +
// Foundation jargon substitution run on it before it ever reaches this client.
//
// NOTE (deferred): the durable backend default is
// `AgentDefinition.default_communication_level` (migration 0112 set all agents
// to `expert`); a data migration flipping it to `novice` is a separate backend
// lane. The session default below is the merchant-VISIBLE default for v1 and is
// what makes a fresh install open in Simple regardless of the agent default,
// because the global pref short-circuits the agent default.
const DEFAULT_COMMUNICATION_PREFERENCE: CommunicationPreference = "novice";

function readCommunicationPreference(): CommunicationPreference {
  try {
    const stored = window.localStorage.getItem(COMM_PREF_KEY);
    if (stored === "follow" || stored === "expert" || stored === "novice") {
      return stored;
    }
  } catch {
    // localStorage unavailable (private mode / SSR / tests) — use default.
  }
  return DEFAULT_COMMUNICATION_PREFERENCE;
}

function writeCommunicationPreference(pref: CommunicationPreference): void {
  try {
    window.localStorage.setItem(COMM_PREF_KEY, pref);
  } catch {
    // Non-fatal: persistence is best-effort.
  }
}

export const useSessionStore = create<SessionState>((set) => ({
  accessToken: INITIAL_TOKEN,
  setAccessToken: (accessToken) => {
    writePersistedAccessToken(accessToken);
    set({ accessToken });
  },
  communicationPreference: readCommunicationPreference(),
  setCommunicationPreference: (communicationPreference) => {
    writeCommunicationPreference(communicationPreference);
    set({ communicationPreference });
  },
  billingEnabled: BILLING_ENABLED,
  setBillingEnabled: (billingEnabled) => set({ billingEnabled }),
}));
